/**
 * The names a widget reads that are the sources of one machine port (one series per port, S3, 2026-10-26). A widget
 * keyed by a tag's name reads the org's tag of that name; when two or more of the org's tags share it (one reader's tag
 * on each of two Things, both bound primary on one machine's port), the name has no one tag, and the widget reads that
 * port's one series instead, once the machine's roles resolve the port to its sources (port-binding.ts: sharedNameKeys,
 * sharedNamePorts, namePortResolutions). A name one tag has is read by its name, as before.
 *
 * The org's bindings and the machines' roles are read only when a widget binds such a name: every other widget reads
 * nothing more.
 */
import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { isLiveApi } from "@/lib/api";
import { twinBindings, type TagOption } from "@/lib/api/twin-bindings";
import { equipmentRolesKey, listEquipmentRoles, type EquipmentRole } from "@/lib/api/equipment-roles";
import {
  namePortResolutions,
  sharedNameKeys,
  sharedNamePorts,
  type PortResolution,
  type PortTags,
} from "./port-binding";

const NONE: ReadonlyMap<string, PortResolution> = new Map();

/**
 * Each key a widget reads by a name the sources of one machine port share, resolved to that port's series; and whether
 * that is still being worked out (until then the key reads one of the tags of that name).
 */
export function useNamePorts(
  keys: readonly string[],
  orgTags: readonly TagOption[] | undefined,
  ports: PortTags | undefined,
): { resolutions: ReadonlyMap<string, PortResolution>; loading: boolean } {
  const shared = useMemo(() => sharedNameKeys(keys, orgTags ?? [], ports), [keys, orgTags, ports]);
  // The same query the alarms' "where" resolver reads (lib/alarms/where.ts), so the two share one fetch.
  const bindings = useQuery({
    queryKey: ["twin-bindings", "all"],
    queryFn: () => twinBindings.list(),
    enabled: isLiveApi && shared.size > 0,
    staleTime: 30_000,
  });
  const at = useMemo(() => sharedNamePorts(shared, bindings.data?.items ?? []), [shared, bindings.data]);
  const entities = useMemo(() => [...new Set([...at.values()].map((a) => a.entityId))].sort(), [at]);
  // The machines' roles, under the key every board reads them by (useEquipmentRoles), so a board's port widgets and its
  // name widgets share them.
  const roles = useQueries({
    queries: entities.map((id) => ({
      queryKey: equipmentRolesKey(id),
      queryFn: () => listEquipmentRoles(id),
      enabled: isLiveApi,
      staleTime: 30_000,
    })),
  });
  // A new array each render: the resolution follows the roles' data by when each was read.
  const stamp = roles.map((r) => r.dataUpdatedAt).join(",");
  const resolutions = useMemo(() => {
    if (at.size === 0) return NONE;
    const byEntity = new Map<string, readonly EquipmentRole[] | undefined>(
      entities.map((id, i) => [id, roles[i]?.data?.roles]),
    );
    return namePortResolutions(at, (id) => byEntity.get(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [at, entities, stamp]);
  return { resolutions, loading: bindings.isLoading || roles.some((r) => r.isLoading) };
}
